import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileArrowUp, DownloadSimple, Eye, EyeSlash, Trash, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import api, { apiError, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import EmployeeForm from "../components/EmployeeForm";

const DOC_KIND_LABELS = { id_copy: "ID / passport", contract: "Contract", offer_letter: "Offer letter" };
const SENSITIVE_LABELS = { id_number: "ID / passport no.", bank_name: "Bank", account_number: "Account number",
                           branch_code: "Branch code", account_type: "Account type", tax_number: "Tax number" };

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [docKind, setDocKind] = useState("id_copy");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [showOffboard, setShowOffboard] = useState(false);
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [offboarding, setOffboarding] = useState(false);
  const { user } = useAuth();

  const offboard = async () => {
    setOffboarding(true);
    try {
      const { data } = await api.post(`/employees/${id}/offboard`, { end_date: endDate });
      toast.success(`Archived — removed from ${data.removed_future_shifts} upcoming shift(s). All history is retained.`);
      setShowOffboard(false);
      load();
    } catch (e) {
      toast.error(apiError(e, "Archive failed"));
    } finally {
      setOffboarding(false);
    }
  };

  const reactivate = async () => {
    try {
      await api.post(`/employees/${id}/reactivate`);
      toast.success("Employee reactivated — they can sign in again");
      load();
    } catch (e) {
      toast.error(apiError(e, "Reactivate failed"));
    }
  };

  const reveal = async () => {
    if (revealed) return setRevealed(null);
    setRevealing(true);
    try {
      const { data } = await api.post(`/employees/${id}/reveal-sensitive`);
      setRevealed(data);
      toast.success("Sensitive fields revealed — this view was logged in the audit trail");
    } catch (e) {
      toast.error(apiError(e, "Reveal failed"));
    } finally {
      setRevealing(false);
    }
  };

  const purgeDoc = async (docId) => {
    try {
      await api.post(`/employees/${id}/documents/${docId}/purge`);
      toast.success("Document permanently deleted (storage bytes destroyed)");
      load();
    } catch (e) {
      toast.error(apiError(e, "Purge failed"));
    }
  };

  const load = () => api.get(`/employees/${id}`).then((r) => setEmp(r.data)).catch(() => toast.error("Failed to load employee"));
  useEffect(() => { load(); }, [id]);

  const invite = async () => {
    setInviting(true);
    try {
      await api.post(`/employees/${id}/onboarding-invite`);
      toast.success("Onboarding invite emailed — the staff member fills in their own details");
    } catch (e) {
      toast.error(apiError(e, "Invite failed"));
    } finally {
      setInviting(false);
    }
  };

  const save = async (payload) => {
    try {
      await api.put(`/employees/${id}`, payload);
      toast.success("Employee updated");
      load();
    } catch (e) {
      toast.error(apiError(e, "Update failed"));
    }
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", docKind);
      fd.append("file", file);
      await api.post(`/employees/${id}/documents`, fd);
      toast.success("Document uploaded");
      setFile(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  if (!emp) return <p className="text-muted-foreground">Loading…</p>;
  const docs = (emp.documents || []).filter((d) => !d.is_deleted);

  return (
    <div className="space-y-8" data-testid="employee-detail">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" data-testid="back-to-employees" onClick={() => navigate("/employees")}>
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-3xl font-bold">{emp.first_name} {emp.last_name}</h1>
          <p className="text-muted-foreground">{emp.position} · <span className="capitalize">{emp.status}</span></p>
        </div>
        <Button variant="outline" size="sm" onClick={invite} disabled={inviting} data-testid="emp-onboarding-invite">
          {inviting ? "Sending…" : "Send onboarding invite"}
        </Button>
        {user.role !== "staff" && emp.status !== "terminated" && emp.role !== "owner" && (
          <Button variant="outline" size="sm" className="text-destructive border-destructive/40" data-testid="emp-archive-btn"
            onClick={() => setShowOffboard(true)}>
            <Archive size={15} className="mr-1" /> Archive employee
          </Button>
        )}
        {emp.status === "terminated" && user.role !== "staff" && (
          <Button variant="outline" size="sm" data-testid="emp-reactivate-btn" onClick={reactivate}>
            <ArrowCounterClockwise size={15} className="mr-1" /> Reactivate
          </Button>
        )}
      </div>

      {emp.status === "terminated" && (
        <div className="rounded-lg bg-[#F87171]/10 border border-[#F87171]/30 px-4 py-3 text-sm text-[#F87171]" data-testid="emp-archived-banner">
          Archived employee — last working day {emp.end_date || "—"}. Login and kiosk access revoked; removed from rosters from that date. All history (shifts, leave, payroll, documents) is retained below.
        </div>
      )}

      <div className="glass border border-border rounded-lg p-6">
        <EmployeeForm initial={emp} onSubmit={save} submitLabel="Save changes" />
      </div>

      {user.role === "owner" && (
        <div className="glass border border-border rounded-lg p-6 space-y-3" data-testid="sensitive-card">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">Sensitive details</h2>
            <Button size="sm" variant="outline" onClick={reveal} disabled={revealing} data-testid="emp-reveal-btn">
              {revealed ? <><EyeSlash size={14} className="mr-1" /> Hide</> : <><Eye size={14} className="mr-1" /> {revealing ? "Revealing…" : "Reveal (logged)"}</>}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Masked by default · every reveal is recorded in the audit log</p>
          {revealed && (
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm" data-testid="emp-revealed-fields">
              {Object.entries(SENSITIVE_LABELS).map(([k, l]) => (
                <div key={k}><dt className="text-xs text-muted-foreground">{l}</dt>
                  <dd className="font-medium" data-testid={`emp-reveal-${k}`}>{revealed[k] || "—"}</dd></div>
              ))}
            </dl>
          )}
        </div>
      )}

      <div className="glass border border-border rounded-lg p-6 space-y-4" data-testid="documents-section">
        <h2 className="font-heading text-lg font-semibold">Documents</h2>
        <div className="flex flex-wrap items-end gap-3">
          <Select value={docKind} onValueChange={setDocKind}>
            <SelectTrigger className="w-48" data-testid="doc-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="id_copy">ID / passport copy</SelectItem>
              <SelectItem value="contract">Signed contract</SelectItem>
            </SelectContent>
          </Select>
          <input type="file" data-testid="doc-file-input" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-accent file:px-3 file:py-2" />
          <Button onClick={upload} disabled={uploading} data-testid="doc-upload-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
            <FileArrowUp size={16} className="mr-1" /> {uploading ? "Uploading…" : "Upload"}
          </Button>
        </div>
        {docs.length === 0 && <p className="text-sm text-muted-foreground">No documents on file.</p>}
        <ul className="space-y-2">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between border-b last:border-0 pb-2" data-testid={`doc-${d.id}`}>
              <div>
                <p className="text-sm font-medium">{d.filename}</p>
                <p className="text-xs text-muted-foreground">{DOC_KIND_LABELS[d.kind] || d.kind} · {(d.size / 1024).toFixed(0)} KB</p>
              </div>
              <div className="flex gap-1">
                <a href={`${API_BASE}/employees/${id}/documents/${d.id}/download`} target="_blank" rel="noreferrer" data-testid={`doc-download-${d.id}`}>
                  <Button variant="outline" size="sm"><DownloadSimple size={16} className="mr-1" /> Open</Button>
                </a>
                {user.role === "owner" && (
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => purgeDoc(d.id)}
                    data-testid={`doc-purge-${d.id}`} title="Permanently delete (storage bytes destroyed)">
                    <Trash size={14} />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <Dialog open={showOffboard} onOpenChange={setShowOffboard}>
        <DialogContent className="max-w-md" data-testid="emp-offboard-dialog">
          <DialogHeader><DialogTitle className="font-heading">Archive {emp.first_name} {emp.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This marks them as a former employee: sign-in and MISE Shift access stop immediately, and they're removed from
              rosters from the last working day forward. <strong>Nothing is deleted</strong> — past shifts, leave, payroll,
              signed documents and reporting stay on record and remain viewable under Staff Directory → Former.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Last working day</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="emp-offboard-date" />
            </div>
            <Button onClick={offboard} disabled={offboarding || !endDate} data-testid="emp-offboard-confirm"
              className="w-full bg-destructive hover:bg-destructive/90 text-white">
              {offboarding ? "Archiving…" : "Archive employee"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
